import React, { useState } from 'react';
import { ArrowRightIcon, BellIcon, CheckIcon, ClockIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { activities, type Activity } from '../../data/loisirs';
const channels = ['Notification', 'Text message', 'Email'];
function AlertOptIn({
  label,
  confirmation
}: {
  label: string;
  confirmation: string;
}) {
  const [armed, setArmed] = useState(false);
  const [channel, setChannel] = useState(channels[0]);
  if (!armed) {
    return <button type="button" onClick={() => setArmed(true)} className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full border border-ville-indigo px-5 py-2.5 text-sm font-bold text-ville-indigo transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1386" data-mp="pvdvlk7ribhm">
        <BellIcon className="h-4 w-4" aria-hidden="true" data-id="element-1387" data-mp="74w4fk2kvnyd" />
        {label}
      </button>;
  }
  return <div className="mt-auto rounded-2xl bg-ville-indigo-soft px-4 py-3" data-id="element-1388" data-mp="261mekcfq3fb">
      <p className="flex items-start gap-2 text-sm font-semibold text-ville-indigo" data-id="element-1389" data-mp="2he9rnkfvds57">
        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1390" data-mp="2mcl1vwa2u4ny" />
        {confirmation}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-1391" data-mp="2r54hszw8lbvx">
        {channels.map(option => <button key={option} type="button" aria-pressed={channel === option} onClick={() => setChannel(option)} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${channel === option ? 'bg-ville-indigo text-white' : 'bg-white text-ville-navy'}`} data-id="element-1392" data-mp="2w2e4w8w5lbgl">
            {option}
          </button>)}
      </div>
    </div>;
}
function ActivityCard({
  activity,
  onRegister
}: {
  activity: Activity;
  onRegister: (activity: Activity) => void;
}) {
  return <article className="flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-1393" data-mp="1oplq1qo833xk">
      <img src={activity.image} alt="" className="h-40 w-full object-cover" data-id="element-1394" data-mp="1jrtpk89ii9hr" />
      <div className="flex flex-1 flex-col p-5" data-id="element-1395" data-mp="1xqtgxnoet2hm">
        <div className="flex items-start justify-between gap-3" data-id="element-1396" data-mp="1xq9v3vvn1tyb">
          <h3 className="text-[17px] font-extrabold leading-tight text-ville-navy" data-id="element-1397" data-mp="1sr73pe8kzm8t">{activity.name}</h3>
          {activity.state === 'complet' && <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600" data-id="element-1398" data-mp="152e497sr2w9g">
              Full
            </span>}
        </div>

        <ul className="mt-3 space-y-1.5 text-sm text-slate-600" data-id="element-1399" data-mp="1espipoa28nul">
          <li className="flex items-center gap-2" data-id="element-1400" data-mp="1a0paz9nr0qng">
            <UsersIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1401" data-mp="19zwbmtr4d3fc" />
            {activity.audience}
          </li>
          <li className="flex items-center gap-2" data-id="element-1402" data-mp="hcjsktk63dqs">
            <MapPinIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1403" data-mp="hc0j818l1xod" />
            {activity.place}
          </li>
          <li className="flex items-center gap-2" data-id="element-1404" data-mp="2sy2bm1bgjzdv">
            <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1405" data-mp="2xrnby00ix5g8" />
            {activity.schedule}
          </li>
        </ul>

        <div className="mt-4 flex items-baseline justify-between gap-3" data-id="element-1406" data-mp="2jxdzjc5mk93m">
          <p className="text-lg font-extrabold text-ville-navy" data-id="element-1407" data-mp="2oyjsnb375ydo">{activity.price}</p>
          <p className="text-xs font-semibold text-slate-500" data-id="element-1408" data-mp="3cp7d0vuaugh4">{activity.availability}</p>
        </div>

        {activity.state === 'a-venir' && activity.opensOn && <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-ville-navy" data-id="element-1409" data-mp="32q855fhcyov6">
            Registration opens {activity.opensOn}
          </p>}

        <div className="mt-5 flex flex-1 flex-col justify-end gap-2" data-id="element-1410" data-mp="1rajgmjoghhs9">
          {activity.state === 'ouvert' && <>
              <button type="button" onClick={() => onRegister(activity)} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1411" data-mp="1w9wc1ste7x6g">
                Register
              </button>
              <button type="button" className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1412" data-mp="2uini4qisedl6">
                View the activity
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1413" data-mp="2givxi6xrlyy7" />
              </button>
            </>}

          {activity.state === 'a-venir' && <AlertOptIn label="Notify me when it opens" confirmation="ATNA will let you know as soon as registration opens." data-id="element-1414" data-mp="19zxq81seltia" />}

          {activity.state === 'complet' && <>
              <button type="button" className="inline-flex w-full items-center justify-center rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1415" data-mp="zzevu0oz5g1j">
                Put me on the waiting list
              </button>
              <AlertOptIn label="Notify me if a spot opens up" confirmation="ATNA is watching for spots and will let you know." data-id="element-1416" data-mp="1iqxww1zfuk0x" />
              <p className="text-center text-xs text-slate-400" data-id="element-1417" data-mp="1smitfklqms3a">Automatic registration options coming soon</p>
            </>}
        </div>
      </div>
    </article>;
}
interface ForYouProps {
  onRegister: (activity: Activity) => void;
}
export function ForYou({
  onRegister
}: ForYouProps) {
  return <section aria-labelledby="recreation-for-you-title" className="mt-14" data-id="element-1418" data-mp="3mdy7f49ihtqk">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1419" data-mp="3hfwqaovnc4hq">
        <div data-id="element-1420" data-mp="716bokap1wb">
          <h2 id="recreation-for-you-title" className="text-2xl font-extrabold tracking-tight text-ville-navy" data-id="element-1421" data-mp="7j6uxdzq2a1">
            For you
          </h2>
          <p className="mt-1 text-[15px] text-slate-600" data-id="element-1422" data-mp="3rf5jhfiotf31">Activities picked for your profile and your family.</p>
        </div>
        <button type="button" className="text-sm font-bold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1423" data-mp="48czg42do599">
          See all activities
        </button>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1424" data-mp="dyobkdw7tkr8">
        {activities.map(activity => <ActivityCard key={activity.id} activity={activity} onRegister={onRegister} data-id="element-1425" data-mp="rvl36hk5r696" />)}
      </div>
    </section>;
}